/**
 * V58 Phase C — Premium Global Map (Apple/Stripe-grade).
 *
 * Replaces the abstract low-poly placeholder with a real, geographically-accurate
 * world map (Natural-Earth 110m topojson, served from /public/maps).
 *
 *  - Real continent silhouettes (no abstract polygons)
 *  - India = central hub (Jabalpur, glowing core)
 *  - Animated great-circle-style arcs flying from India → every supported country
 *  - Traveling spark along each arc once the section is in view
 *  - Pulsing halos around every marker (CSS-only, 60fps, GPU-composited)
 *  - prefers-reduced-motion respected
 *  - Fully responsive — taller aspect on mobile, wider on desktop
 *  - Lightweight: topojson ≈ 30 KB gzip, served same-origin, cached
 *  - DESIGN-ONLY: identical data, copy, schema, structure preserved
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { geoEqualEarth } from "d3-geo";
import { Globe2, MapPin } from "lucide-react";
import "../../styles/v2-tokens.css";

/** Capital-city coordinates [lng, lat] for served countries (decorative + real). */
const SERVED = [
    { name: "India",          to: "/online-classes",                   lng: 79.0,   lat: 21.0,  flag: "🇮🇳", primary: true },
    { name: "United States",  to: "/online-tuition-usa",               lng: -98.5,  lat: 38.5,  flag: "🇺🇸" },
    { name: "United Kingdom", to: "/online-tuition-uk",                lng: -2.0,   lat: 54.0,  flag: "🇬🇧" },
    { name: "Canada",         to: "/online-tuition-canada",            lng: -106.0, lat: 56.0,  flag: "🇨🇦" },
    { name: "Australia",      to: "/online-tuition-australia",         lng: 134.0,  lat: -25.0, flag: "🇦🇺" },
    { name: "UAE",            to: "/online-tuition-uae",               lng: 54.0,   lat: 24.0,  flag: "🇦🇪" },
    { name: "Singapore",      to: "/online-tuition-singapore",         lng: 103.8,  lat: 1.35,  flag: "🇸🇬" },
    { name: "New Zealand",    to: "/online-tuition-new-zealand",       lng: 174.0,  lat: -41.0, flag: "🇳🇿" },
    { name: "Saudi Arabia",   to: "/online-tuition-saudi-arabia",      lng: 45.0,   lat: 24.0,  flag: "🇸🇦" },
    { name: "Qatar",          to: "/online-tuition-qatar",             lng: 51.0,   lat: 25.3,  flag: "🇶🇦" },
    { name: "Kuwait",         to: "/online-tuition-kuwait",            lng: 47.5,   lat: 29.3,  flag: "🇰🇼" },
    { name: "Oman",           to: "/online-tuition-oman",              lng: 57.0,   lat: 21.0,  flag: "🇴🇲" },
    { name: "Bahrain",        to: "/online-tuition-bahrain",           lng: 50.5,   lat: 26.0,  flag: "🇧🇭" },
    { name: "Germany",        to: "/online-tuition-germany",           lng: 10.5,   lat: 51.0,  flag: "🇩🇪" },
];

// Same dimensions react-simple-maps uses by default — we keep them in sync so the
// projection we build locally matches the one used by <ComposableMap>.
const MAP_W = 980;
const MAP_H = 520;
const projection = geoEqualEarth().scale(165).translate([MAP_W / 2, MAP_H / 2 + 20]);
const HQ = projection([79.0, 21.0]); // [px, px]

const GEO_URL = "/maps/world-110m.json";
// India-specific GeoJSON with FULL official boundaries (J&K, Ladakh, Arunachal Pradesh).
// Overlayed on top of the world map so India is rendered with its complete shape
// instead of the de-facto truncated outline shipped by Natural Earth 110m.
const INDIA_GEO_URL = "/maps/india-full.geojson";

export default function GlobalMap() {
    const sectionRef = useRef(null);
    const [animateNow, setAnimateNow] = useState(false);

    // Trigger animations only once the section scrolls into view.
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return undefined;
        const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (prefersReduced) { setAnimateNow(true); return undefined; }
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setAnimateNow(true);
                    obs.disconnect();
                }
            },
            { rootMargin: "-15% 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            data-testid="v2-global-map"
            className="v2-page v2-body relative bg-[#0a1f4d] text-white py-24 sm:py-32 lg:py-40 overflow-hidden"
        >
            {/* Ambient radial glow */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_30%,rgba(37,99,235,0.22)_0%,rgba(10,31,77,0)_70%)]" />
            {/* Subtle starfield noise */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-screen"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                {/* Header */}
                <motion.header
                    initial={{ y: 24, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="max-w-3xl mb-12 sm:mb-16"
                >
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#E0F2FE]/80 mb-4">
                        <Globe2 className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
                        Reach
                    </p>
                    <h2 className="v2-display font-medium text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                        Indian classrooms.
                        <br /><span className="text-white/65">Global students.</span>
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
                        Headquartered in Jabalpur, delivering 1:1 classes to families across 44+ Indian cities and 14 countries — with timezone-perfect scheduling and India&apos;s top tutors.
                    </p>
                </motion.header>

                {/* Map shell with subtle border + glow */}
                <div className="relative rounded-[28px] border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent backdrop-blur-[2px] p-3 sm:p-5 lg:p-6 shadow-[0_40px_120px_-40px_rgba(37,99,235,0.45)]">
                    {/* Top-left/right stat overlays — float on desktop only */}
                    <div className="hidden lg:flex absolute top-6 left-6 right-6 justify-between pointer-events-none z-[2]">
                        <StatChip number="44+" label="Indian cities" />
                        <StatChip number="14" label="Countries served" align="right" />
                    </div>

                    {/* Mobile-only stat chips above the map */}
                    <div className="lg:hidden grid grid-cols-3 gap-2 mb-4 px-1">
                        <StatChip number="44+" label="Indian cities" tiny />
                        <StatChip number="14" label="Countries" tiny />
                        <StatChip number="50K+" label="Students" tiny />
                    </div>

                    <div className="relative">
                        <ComposableMap
                            projection="geoEqualEarth"
                            projectionConfig={{ scale: 165, center: [0, 0] }}
                            width={MAP_W}
                            height={MAP_H}
                            style={{ width: "100%", height: "auto", maxHeight: "70vh" }}
                            aria-hidden="true"
                        >
                            {/* Ocean / background subtle */}
                            <defs>
                                <linearGradient id="v2gm-arc" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#FBBF24" stopOpacity="0.0" />
                                    <stop offset="15%" stopColor="#FBBF24" stopOpacity="0.95" />
                                    <stop offset="55%" stopColor="#60A5FA" stopOpacity="0.95" />
                                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.55" />
                                </linearGradient>
                                <radialGradient id="v2gm-glow-hq">
                                    <stop offset="0%" stopColor="#FBBF24" stopOpacity="0.55" />
                                    <stop offset="60%" stopColor="#FBBF24" stopOpacity="0.05" />
                                    <stop offset="100%" stopColor="#FBBF24" stopOpacity="0" />
                                </radialGradient>
                                <radialGradient id="v2gm-glow-node">
                                    <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.65" />
                                    <stop offset="60%" stopColor="#60A5FA" stopOpacity="0.08" />
                                    <stop offset="100%" stopColor="#60A5FA" stopOpacity="0" />
                                </radialGradient>
                            </defs>

                            {/* Latitude/longitude grid — subtle */}
                            <g pointerEvents="none">
                                {[120, 200, 280, 360, 440].map((y) => (
                                    <line key={`lat${y}`} x1="0" x2={MAP_W} y1={y} y2={y} stroke="rgba(255,255,255,0.025)" strokeWidth="1" />
                                ))}
                                {[160, 320, 490, 660, 820].map((x) => (
                                    <line key={`lng${x}`} x1={x} x2={x} y1="40" y2={MAP_H - 40} stroke="rgba(255,255,255,0.025)" strokeWidth="1" />
                                ))}
                            </g>

                            {/* Real continent geography */}
                            <Geographies geography={GEO_URL}>
                                {({ geographies }) =>
                                    geographies.map((geo) => (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            style={{
                                                default: {
                                                    fill: "rgba(255,255,255,0.06)",
                                                    stroke: "rgba(96,165,250,0.22)",
                                                    strokeWidth: 0.45,
                                                    outline: "none",
                                                },
                                                hover: {
                                                    fill: "rgba(255,255,255,0.10)",
                                                    outline: "none",
                                                },
                                                pressed: { outline: "none" },
                                            }}
                                        />
                                    ))
                                }
                            </Geographies>

                            {/* India overlay — full official boundaries (J&K, Ladakh, Arunachal Pradesh).
                                Drawn on top of the world layer so India is rendered with its
                                complete claimed shape rather than the truncated default. */}
                            <Geographies geography={INDIA_GEO_URL}>
                                {({ geographies }) =>
                                    geographies.map((geo) => (
                                        <Geography
                                            key={`india-${geo.rsmKey}`}
                                            geography={geo}
                                            style={{
                                                default: {
                                                    fill: "rgba(96,165,250,0.22)",
                                                    stroke: "rgba(147,197,253,0.6)",
                                                    strokeWidth: 0.55,
                                                    outline: "none",
                                                },
                                                hover: {
                                                    fill: "rgba(96,165,250,0.30)",
                                                    outline: "none",
                                                },
                                                pressed: { outline: "none" },
                                            }}
                                        />
                                    ))
                                }
                            </Geographies>

                            {/* Arcs from HQ → every country */}
                            <g pointerEvents="none">
                                {SERVED.filter((c) => !c.primary).map((c, i) => {
                                    const dest = projection([c.lng, c.lat]);
                                    if (!dest || !HQ) return null;
                                    const [x1, y1] = HQ;
                                    const [x2, y2] = dest;
                                    const dx = x2 - x1;
                                    const dy = y2 - y1;
                                    const mx = x1 + dx / 2;
                                    const lift = Math.min(140, Math.abs(dx) * 0.22 + 30);
                                    const my = y1 + dy / 2 - lift;
                                    const d = `M${x1},${y1} Q${mx},${my} ${x2},${y2}`;
                                    return (
                                        <g key={c.name}>
                                            {/* faint base line */}
                                            <path d={d} fill="none" stroke="rgba(96,165,250,0.18)" strokeWidth="0.9" />
                                            {/* animated arc */}
                                            <path
                                                d={d}
                                                fill="none"
                                                stroke="url(#v2gm-arc)"
                                                strokeWidth="1.4"
                                                strokeLinecap="round"
                                                style={{
                                                    strokeDasharray: 600,
                                                    strokeDashoffset: animateNow ? 0 : 600,
                                                    transition: `stroke-dashoffset 1.8s cubic-bezier(0.22,1,0.36,1) ${0.12 * i + 0.2}s`,
                                                    filter: "drop-shadow(0 0 4px rgba(96,165,250,0.4))",
                                                }}
                                            />
                                            {/* traveling spark dot */}
                                            {animateNow && (
                                                <circle r="2.4" fill="#FBBF24">
                                                    <animateMotion
                                                        dur="3.6s"
                                                        begin={`${0.12 * i + 0.4}s`}
                                                        repeatCount="indefinite"
                                                        path={d}
                                                        rotate="auto"
                                                    />
                                                    <animate
                                                        attributeName="opacity"
                                                        values="0;1;1;0"
                                                        keyTimes="0;0.15;0.85;1"
                                                        dur="3.6s"
                                                        begin={`${0.12 * i + 0.4}s`}
                                                        repeatCount="indefinite"
                                                    />
                                                </circle>
                                            )}
                                        </g>
                                    );
                                })}
                            </g>

                            {/* Country markers */}
                            <g pointerEvents="none">
                                {SERVED.map((c, i) => {
                                    const p = projection([c.lng, c.lat]);
                                    if (!p) return null;
                                    const [x, y] = p;
                                    const isHQ = !!c.primary;
                                    return (
                                        <g
                                            key={c.name}
                                            transform={`translate(${x} ${y})`}
                                            style={{
                                                opacity: animateNow ? 1 : 0,
                                                transform: `translate(${x}px, ${y}px) scale(${animateNow ? 1 : 0})`,
                                                transition: `opacity 0.55s ease-out ${0.06 * i + 0.1}s, transform 0.75s cubic-bezier(0.34,1.56,0.64,1) ${0.06 * i + 0.1}s`,
                                                transformOrigin: `${x}px ${y}px`,
                                            }}
                                        >
                                            {/* outer glow */}
                                            <circle r={isHQ ? 26 : 14} fill={isHQ ? "url(#v2gm-glow-hq)" : "url(#v2gm-glow-node)"} />
                                            {/* pulsing ring */}
                                            <circle
                                                r={isHQ ? 9 : 5.5}
                                                fill="none"
                                                stroke={isHQ ? "#FBBF24" : "#93C5FD"}
                                                strokeWidth="1.4"
                                                opacity="0.85"
                                                className="v2gm-pulse"
                                                style={{ animationDelay: `${i * 0.18}s` }}
                                            />
                                            {/* core dot */}
                                            <circle
                                                r={isHQ ? 5 : 3.2}
                                                fill={isHQ ? "#FBBF24" : "#FFFFFF"}
                                                stroke={isHQ ? "#FFFFFF" : "#2563EB"}
                                                strokeWidth={isHQ ? 1.8 : 1.2}
                                            />
                                        </g>
                                    );
                                })}
                            </g>
                        </ComposableMap>

                        {/* HQ label badge — anchored to India over the map */}
                        <HQBadge />
                    </div>

                    {/* Country chips — now real <Link> elements for SEO + accessibility */}
                    <ul className="mt-10 sm:mt-14 flex flex-wrap gap-2 max-w-5xl mx-auto justify-center" aria-label="Countries served">
                        {SERVED.map((c) => (
                            <li key={c.name}>
                                <Link
                                    to={c.to}
                                    data-testid={`v2-globalmap-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    aria-label={`Online tuition in ${c.name}`}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight border transition-all duration-200 ${
                                        c.primary
                                            ? "bg-amber-400 text-[#0a1f4d] border-amber-300 shadow-[0_6px_24px_-6px_rgba(251,191,36,0.5)] hover:bg-amber-300 hover:shadow-[0_8px_28px_-6px_rgba(251,191,36,0.65)]"
                                            : "bg-white/5 text-white/85 border-white/15 hover:bg-white/15 hover:border-white/30 hover:text-white"
                                    }`}
                                >
                                    <span aria-hidden>{c.flag}</span> {c.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* CSS for pulsing ring + GPU compositing hints */}
            <style>{`
                @keyframes v2gmPulse {
                    0%   { transform: scale(0.6); opacity: 0.85; }
                    70%  { transform: scale(2.4); opacity: 0; }
                    100% { transform: scale(2.4); opacity: 0; }
                }
                .v2gm-pulse {
                    transform-box: fill-box;
                    transform-origin: center;
                    animation: v2gmPulse 2.6s ease-out infinite;
                    will-change: transform, opacity;
                }
                @media (prefers-reduced-motion: reduce) {
                    .v2gm-pulse { animation: none; }
                }
            `}</style>
        </section>
    );
}

function StatChip({ number, label, align = "left", tiny = false }) {
    return (
        <div
            className={`pointer-events-auto inline-flex flex-col rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur-md px-3 py-2 ${
                tiny ? "items-center text-center" : align === "right" ? "items-end text-right" : "items-start text-left"
            }`}
        >
            <span className={`v2-display font-medium text-amber-300 ${tiny ? "text-lg" : "text-xl"} leading-none tabular-nums`}>
                {number}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">{label}</span>
        </div>
    );
}

function HQBadge() {
    // Position the badge near the India hub using the projection.
    if (!HQ) return null;
    const [hx, hy] = HQ;
    const leftPct = (hx / MAP_W) * 100;
    const topPct = (hy / MAP_H) * 100;
    return (
        <div
            className="absolute hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0a1f4d] border border-amber-300/40 shadow-[0_8px_28px_-4px_rgba(251,191,36,0.45)] pointer-events-none"
            style={{
                left: `calc(${leftPct}% + 12px)`,
                top: `calc(${topPct}% - 10px)`,
                transform: "translate(0, -100%)",
            }}
        >
            <MapPin className="w-3 h-3 text-amber-300" aria-hidden />
            <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-white">Jabalpur · HQ</span>
        </div>
    );
}
